import moment from 'moment';
import React, { useMemo, useState, useEffect } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { BtnComponent } from '../../components/button';
import MainPageDialog, { PageDialogContent, PageDialogFooter } from '../../components/dialog';
import { useGroupChatContext } from '../../context/groupchatContext';
import { chatApi, useGetEncountersQuery } from '../../services/chatService';
import EncounterPreview from './encounterPreview';
import { FaEye } from "react-icons/fa";
import EncounterFilter from './encounterFilters';
import Pagination from '../../components/pagination';
import { MidAtlanticPracticeID } from '../../utils/constant';

const EncounterList = React.memo(({ closeEncounterPopup }) => {
    const {
        practice: { id: practice_id },
        auth: authToken,
    } = JSON.parse(localStorage.getItem("auth"));

    const groupChatContext = useGroupChatContext();
    const { selectedChat } = useMemo(() => ({
        selectedChat: groupChatContext.selectedChat,
    }));

    const [isFetchingData, setIsFetchingData] = useState(false);
    const [previewEncounterData, setPreviewEncounterData] = useState({ isOpen: false, data: null })

    const [postsPerPage] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);
    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
    };
    const [filters, setFilters] = useState({});

    const date = filters?.from_date
        ? moment(filters.from_date).format('YYYY-MM-DD') +
        (filters.to_date ? ',' + moment(filters.to_date).format('YYYY-MM-DD') : '')
        : '';

    const fetchEncounterData = useGetEncountersQuery({
        pid: selectedChat.pid,
        limit: postsPerPage,
        page: currentPage,
        case_id: filters?.case_id || '',
        provider_id: filters?.provider_id || '',
        visit_reason: filters?.visit_reason || '',
        serial_number: filters?.serial_number || '',
        template: filters?.template || '',
        encounter_types: filters?.encounter_types || '',
        encounter_type: filters?.encounter_types || '',
        referer: "search_encounters",
        date: date,
    });

    const { data: encounterDataToList, isLoading } = fetchEncounterData;

    const encounterData = encounterDataToList?.data?.filter(item => {
        if ((practice_id != MidAtlanticPracticeID && item?.status == 'draft') || (item?.deleted == "1" || item?.deleted == 1)) {
            return false; // Exclude encounters status 'draft' if not Mid Atlantic practice
        }
        return true;
    });


    const [getEncounterPreview] = chatApi.endpoints.getEncounterPreview.useLazyQuery();

    const previewEncounter = async (encounter) => {

        setIsFetchingData(true)
        const response = await getEncounterPreview({ pid: selectedChat.pid, encounter: encounter.id });
        setIsFetchingData(false)

        if (response.data) {
            setPreviewEncounterData({ isOpen: true, data: response.data })
        }
    }

    const closePreviewEncounter = () => {
        setPreviewEncounterData({ isOpen: false })
    }

    useEffect(() => {
        if (selectedChat.pid) {
            fetchEncounterData.refetch();
        }
    }, [filters, currentPage, postsPerPage, selectedChat.pid]);

    return (
        <>
            <PageDialogContent
                contentCls="pt-2 px-5"
                hideOverFlow={true}
                contentScroll={false}>
                <div className="w-full text-gray-500 h-[70vh] flex flex-col">
                    {(isFetchingData || isLoading) ?
                        <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto absolute inset-0 outline-none focus:outline-none before:absolute before:flex before:w-full before:h-full z-[999]">
                            <FaSpinner className="animate-spin text-green-300-500 ml-1" size={40} />
                        </div>
                        : null
                    }
                    <div className="flex-shrink-0 pb-2">
                        <EncounterFilter setFilters={setFilters} isLoading={isLoading} />
                    </div>
                    
                    <div className=" overflow-x-auto overflow-y-auto w-[300px] md:w-auto">
                        <div className="table w-full">

                        <div className='flex-shrink-0 '>
                            <div className='app-listing-header gap-2 py-1 px-[0.1rem] flex justify-between align-middle text-start'>
                                <div className='w-[160px] md:w-[9%]'>Encounter Date</div>
                                <div className='w-[160px] md:w-[11%]'>Visit Reason/Case</div>
                                <div className='w-[160px] md:w-2/12'>Encounter Type/ID</div>
                                <div className='w-[160px] md:w-2/12'>Provider</div>
                                <div className='md:w-2/12'>Location</div>
                                <div className='w-[160px] md:w-1/12'>CPTs</div>
                                <div className='w-[160px] md:w-1/12'>ICDs</div>
                                <div className='w-[160px] md:w-1/12'>Status</div>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto overflow-x-hidden">
                            {encounterData?.map((item, index) => (
                                <div key={index} className='border-b-[0.5px] py-2 flex justify-between align-middle text-start gap-2'>
                                    <div className='w-[160px] md:w-[9%]'>{moment(item.date).format('MM/DD/YYYY hh:mm A')}</div>
                                    <div className='w-[160px] md:w-[11%]'>
                                        {item.visit_reason_title ? item.visit_reason_title : 'N/A'}
                                        <p>{item.case_title}</p>
                                    </div>
                                    <div className='w-[160px] md:w-2/12 uppercase'>
                                        {item.encounter_type_title ? item.encounter_type_title : item.type}
                                        <p>{item.serial_number}</p>
                                    </div>
                                    <div className='w-[160px] md:w-2/12'>{item.provider_name}</div>
                                    <div className='w-[160px] md:w-2/12'>{item.Location?.name}</div>
                                    <div className='w-[160px] md:w-1/12'>{item.enm_codes?.map(cpt => cpt.cpt_code).join(', ')}</div>
                                    <div className='w-[160px] md:w-1/12'>{item.icd_10_codes?.slice(0, 4)?.map(icd => icd.code).join(', ')}</div>
                                    <div className='w-[160px] md:w-1/12 flex justify-between pr-2'>
                                        <span className={`first-letter:uppercase rounded-md h-fit p-1 ${item.status == 'draft' ? 'draft_encounter' : 'sign_encounter'}`}>{item.status}</span>
                                        <span className='cursor-pointer text-blue-500' title='Preview Encounter' onClick={() => previewEncounter(item)}>
                                            <FaEye size='20' />
                                        </span>
                                    </div>
                                </div>
                            ))}
                            {!encounterData?.length ?
                                <div className="container mx-auto py-8">
                                    <p className="text-gray-600 text-lg text-left md:text-center">Not Recorded.</p>
                                </div>
                                : null
                            }
                        </div>
                        </div>
                    </div>

                    
                    {encounterData?.length > 0 && (
                        <div className="flex-shrink-0 pt-2 border-t">
                            <Pagination
                                recordPerPage={postsPerPage}
                                currentPage={currentPage}
                                paginate={paginate}
                                totalRecords={encounterData?.length || 0}
                                setCurrentPage={setCurrentPage}
                            />
                        </div>
                    )}
                </div>
            </PageDialogContent>
            <PageDialogFooter>
                <BtnComponent text="Close" btnType="default" onClick={closeEncounterPopup} />
            </PageDialogFooter>


            {previewEncounterData.isOpen ?
                <MainPageDialog
                    title={`${moment(previewEncounterData.data?.encounter_data?.created).format('MM/DD/YYYY')} - ${previewEncounterData.data?.encounter_data?.encounter_type_title ? previewEncounterData.data?.encounter_data?.encounter_type_title : ''}`}
                    onCancelHandler={closePreviewEncounter}
                    customHeight="min-h-[80vh] overflow-y-auto"
                    component={
                        <EncounterPreview closeHandler={closePreviewEncounter} encounterData={previewEncounterData.data} />
                    }
                    footer={false}
                >
                </MainPageDialog>
                : null
            }
        </>
    )
})

export default EncounterList;
